import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageSquareText, Plus, Trash2, Save, Copy, Send, Smartphone, ShieldCheck, ShieldOff } from "lucide-react";

const QUICK = ["HELP", "STATUS", "JOBS", "DISPATCH T-01 "];

/* Settings → Emergency SMS: whitelist, Twilio status, webhook URL, phone-style simulator, log. */
export default function EmergencySmsCard({ canEdit }) {
  const [cfg, setCfg] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [log, setLog] = useState([]);
  const [from, setFrom] = useState("");
  const [body, setBody] = useState("");
  const [thread, setThread] = useState([]);
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([api.get("/sms/config"), api.get("/sms/log?limit=20")]);
      setCfg(c.data);
      setNumbers(c.data.numbers || []);
      setLog(l.data || []);
      setFrom((f) => f || c.data.numbers?.[0]?.phone || "");
    } catch (e) { /* role without access */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { threadRef.current?.scrollTo({ top: 1e6 }); }, [thread]);

  const update = (i, patch) => { setNumbers((n) => n.map((x, k) => (k === i ? { ...x, ...patch } : x))); setDirty(true); };
  const add = () => { setNumbers((n) => [...n, { phone: "", label: "", can_dispatch: true }]); setDirty(true); };
  const remove = (i) => { setNumbers((n) => n.filter((_, k) => k !== i)); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/sms/whitelist", { numbers: numbers.filter((n) => n.phone.trim()) });
      setNumbers(data.numbers); setDirty(false);
      toast.success(`Whitelist saved · ${data.numbers.length} number${data.numbers.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const send = async (text) => {
    const msg = (text ?? body).trim();
    if (!from.trim()) return toast.error("Enter the phone number the text would come from");
    if (!msg) return toast.error("Type a command, e.g. STATUS");
    setBusy(true);
    setThread((t) => [...t, { dir: "out", text: msg, at: new Date() }]);
    setBody("");
    try {
      const { data } = await api.post("/sms/simulate", { from_phone: from, body: msg });
      setThread((t) => [...t, { dir: "in", text: data.reply, at: new Date(), authorised: data.authorised, action: data.action }]);
      if (data.action) toast.success(`SMS dispatch applied · ${data.action.truck} → job #${(data.action.job_id || "").slice(0, 4).toUpperCase()}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  if (!cfg) return null;

  return (
    <div className="card-industrial p-6" data-testid="emergency-sms-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tag mb-1 flex items-center gap-2"><MessageSquareText className="w-3.5 h-3.5 text-primary-orange" /> Emergency SMS Dispatch · Failsafe</div>
          <h2 className="heading text-lg font-black uppercase">Text commands when the board is down</h2>
          <p className="text-xs text-muted-industrial mt-1 max-w-2xl">
            Whitelisted phones can text <span className="mono text-ink">STATUS</span>, <span className="mono text-ink">JOBS</span> or
            <span className="mono text-ink"> DISPATCH &lt;truck&gt; &lt;job#|site&gt;</span> to the plant number and the system replies like a dispatcher would.
            Numbers marked <em>view only</em> (drivers) cannot dispatch.
          </p>
        </div>
        <span data-testid="sms-transport-status"
          className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${cfg.configured ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" : "border-amber-500 text-amber-400 bg-amber-500/10"}`}>
          {cfg.configured ? `Twilio live · ${cfg.from_number}` : "Mocked · add Twilio keys to go live"}
        </span>
      </div>

      {cfg.country_mismatch && (
        <div className="mt-3 border-l-4 border-red-500 bg-red-500/10 px-3 py-2.5" data-testid="sms-country-mismatch">
          <div className="mono text-[10px] uppercase tracking-widest text-red-400 font-black">
            {cfg.from_country} sending number · your plant is {cfg.local_country}
          </div>
          <div className="text-xs text-ink mt-1 leading-relaxed">
            You're texting customers from <span className="mono text-red-300">{cfg.from_number}</span>, a {cfg.from_country} number.
            Replying to it is an <strong>international SMS</strong> for the customer — charged at overseas rates, and blocked outright on
            many prepaid and business plans, so some answers will never reach you. Carriers also filter foreign numbers harder, so the
            warning itself can be dropped.
          </div>
          <div className="text-xs text-muted-industrial mt-1.5">
            Fix: buy a {cfg.local_country} number in the Twilio console, point its <em>A message comes in</em> webhook at the URL below,
            then update <span className="mono">TWILIO_FROM_NUMBER</span>. No code change needed. Note that alphanumeric sender IDs
            cannot receive replies at all.
          </div>
        </div>
      )}

      <div className="mt-4 grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="label-tag">Authorised numbers</Label>
              {canEdit && <button type="button" onClick={add} data-testid="sms-add-number" className="mono text-[10px] uppercase tracking-widest text-primary-orange inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add number</button>}
            </div>
            {numbers.length === 0 && <div className="border border-dashed border-industrial p-3 text-xs text-muted-industrial" data-testid="sms-whitelist-empty">No numbers yet — add the dispatcher's and drivers' mobiles.</div>}
            <div className="space-y-2">
              {numbers.map((n, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center" data-testid={`sms-number-row-${i}`}>
                  <Input value={n.label} disabled={!canEdit} onChange={(e) => update(i, { label: e.target.value })} placeholder="Label (e.g. Matt · Dispatch)" className="rounded-none bg-transparent border-industrial mono h-9 text-xs" data-testid={`sms-number-label-${i}`} />
                  <Input value={n.phone} disabled={!canEdit} onChange={(e) => update(i, { phone: e.target.value })} placeholder="04xx xxx xxx" className="rounded-none bg-transparent border-industrial mono h-9 text-xs" data-testid={`sms-number-phone-${i}`} />
                  <button type="button" disabled={!canEdit} onClick={() => update(i, { can_dispatch: !n.can_dispatch })} data-testid={`sms-number-dispatch-${i}`}
                    title={n.can_dispatch ? "Can DISPATCH trucks — click to make view only" : "View only (STATUS / JOBS) — click to allow DISPATCH"}
                    className={`h-9 px-2 border mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1 ${n.can_dispatch ? "border-emerald-500/70 text-emerald-400" : "border-industrial text-muted-industrial"}`}>
                    {n.can_dispatch ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}{n.can_dispatch ? "Dispatch" : "View only"}
                  </button>
                  {canEdit ? <button type="button" onClick={() => remove(i)} data-testid={`sms-number-remove-${i}`} className="h-9 px-2 text-muted-industrial hover:text-red-400"><Trash2 className="w-4 h-4" /></button> : <span />}
                </div>
              ))}
            </div>
            {canEdit && dirty && (
              <Button onClick={save} disabled={busy} data-testid="sms-save-whitelist" className="mt-3 rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs h-9"><Save className="w-3.5 h-3.5 mr-1" /> Save whitelist</Button>
            )}
          </div>

          <div className="border border-industrial p-3 space-y-2">
            <div className="label-tag">Twilio setup</div>
            <div className="text-xs text-muted-industrial">In the Twilio console set your number's <em>Messaging → A message comes in</em> webhook (HTTP POST) to:</div>
            <div className="flex items-center gap-2">
              <code className="mono text-[11px] text-ink break-all flex-1" data-testid="sms-webhook-url">{cfg.webhook_url}</code>
              <button type="button" onClick={() => copy(cfg.webhook_url)} data-testid="sms-copy-webhook" className="p-1.5 border border-industrial hover:border-primary-orange"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <div className="text-[11px] text-muted-industrial">Backend env: <span className="mono">TWILIO_ACCOUNT_SID · TWILIO_AUTH_TOKEN · TWILIO_FROM_NUMBER</span>. Until then the listener is {" "}
              <span className="text-amber-400 font-bold">MOCKED</span> — use the simulator to verify commands.</div>
          </div>

          <div className="border border-industrial p-3">
            <div className="label-tag mb-1">Commands</div>
            <table className="w-full text-xs">
              <tbody>
                {(cfg.commands || []).map((c) => (
                  <tr key={c.cmd} className="border-b border-industrial/50 last:border-0"><td className="py-1 pr-3 mono text-primary-orange whitespace-nowrap">{c.cmd}</td><td className="py-1 text-muted-industrial">{c.desc}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-industrial bg-surface-2/60 p-3" data-testid="sms-simulator">
            <div className="flex items-center justify-between mb-2">
              <div className="label-tag flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 text-primary-orange" /> Test SMS command · simulator</div>
              <span className="mono text-[9px] uppercase tracking-widest text-amber-400">same engine · no Twilio needed</span>
            </div>
            <div className="mb-2">
              <Label className="label-tag">From number</Label>
              <div className="flex gap-2">
                <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="+61412345678" className="rounded-none bg-transparent border-industrial mono h-9 text-xs" data-testid="sms-sim-from" list="sms-whitelist-options" />
                <datalist id="sms-whitelist-options">{numbers.filter((n) => n.phone).map((n) => <option key={n.phone} value={n.phone}>{n.label}</option>)}</datalist>
              </div>
            </div>
            <div ref={threadRef} className="h-56 overflow-y-auto border border-industrial/60 p-2 space-y-2 bg-[rgb(var(--bg))]" data-testid="sms-sim-thread">
              {thread.length === 0 && <div className="text-[11px] text-muted-industrial text-center pt-16">Send STATUS, JOBS or a DISPATCH command to see the reply a phone would get.</div>}
              {thread.map((m, i) => (
                <div key={i} className={`flex ${m.dir === "out" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={m.dir === "out" ? "sms-sim-sent" : "sms-sim-reply"}
                    className={`max-w-[85%] px-3 py-2 text-xs whitespace-pre-wrap leading-snug ${m.dir === "out" ? "bg-primary-orange text-black" : m.authorised === false ? "bg-red-900/60 text-red-100 border border-red-600" : "bg-surface-2 text-ink border border-industrial"}`}>
                    {m.text}
                    <div className={`mono text-[9px] mt-1 ${m.dir === "out" ? "text-black/60" : "text-muted-industrial"}`}>{m.at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{m.dir === "in" ? " · ArmorBatch" : ` · ${from}`}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {QUICK.map((q) => <button key={q} type="button" onClick={() => (q.endsWith(" ") ? setBody(q) : send(q))} data-testid={`sms-quick-${q.trim().split(" ")[0].toLowerCase()}`} className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-primary-orange hover:text-primary-orange">{q.trim()}</button>)}
            </div>
            <form className="flex gap-2 mt-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
              <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a text command…" className="rounded-none bg-transparent border-industrial mono h-10 text-sm" data-testid="sms-sim-body" />
              <Button type="submit" disabled={busy} data-testid="sms-sim-send" className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black h-10 px-4"><Send className="w-4 h-4" /></Button>
            </form>
          </div>

          <div>
            <div className="label-tag mb-1">Recent SMS log</div>
            {log.length === 0 && <div className="text-xs text-muted-industrial" data-testid="sms-log-empty">No texts yet.</div>}
            <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="sms-log">
              {log.map((r, i) => (
                <div key={i} className="border border-industrial/60 px-2 py-1.5 text-[11px]" data-testid="sms-log-row">
                  <div className="flex items-center gap-2 mono text-[9px] uppercase tracking-widest text-muted-industrial">
                    <span>{new Date(r.at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    <span className="text-ink">{r.actor || r.from}</span>
                    <span className={r.channel === "twilio" ? "text-emerald-400" : "text-amber-400"}>{r.channel}</span>
                    {r.authorised === false && <span className="text-red-400">unauthorised</span>}
                    {r.action && <span className="text-primary-orange">dispatched {r.action.truck}</span>}
                  </div>
                  <div className="mono text-ink">› {r.body}</div>
                  <div className="text-muted-industrial truncate">‹ {r.reply}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
